<template>
  <Teleport to="body">
    <div class="tutorial-popup-overlay" v-if="merchant" v-loading="loadingData">

      <!-- Step 0: intro -->
      <div v-if="step === 0 && !socialStep" class="tutorial-popup tutorial-popup--intro">
        <el-image class="tutorial-popup__logo" :src="merchant.brand_logo" fit="contain" />
        <h2 class="tutorial-popup__title">{{ content.title }}</h2>
        <p class="tutorial-popup__desc">{{ content.description }}</p>
        <el-button type="primary" size="large" class="tutorial-popup__btn w-100" @click="handleGetStarted">
          Next
        </el-button>
      </div>

      <!-- Step 2-3: Authenticate social accounts -->
      <div v-else-if="socialStep === '2-3'" class="tutorial-popup tutorial-popup--social">
        <h2 class="w-100 big-title">Authenticate your social accounts</h2>
        <p class="w-100 hint-text">
          Please connect at least one social account in order to activate your storefront experience and personalize your storefront URL.
        </p>
        <VeriftySocial :type="'2-3'" />
        <div class="btn-box">
          <el-button class="ui-button--w100" size="large" type="primary" @click="handleSocialNext">Next</el-button>
        </div>
      </div>

      <!-- Step 2-4: Select handle -->
      <div v-else-if="socialStep === '2-4'" class="tutorial-popup tutorial-popup--social">
        <h2 class="w-100">Personalize Your Storefront URL</h2>
        <VeriftySocial ref="verifySocialRef" :type="'2-4'" @updateHandle="updateHandle" />
        <div class="btn-box">
          <el-button class="ui-button--w100" size="large" type="primary" @click="saveHandle">Done</el-button>
        </div>
      </div>

      <!-- Step: tutorial slide -->
      <div v-else class="tutorial-popup tutorial-popup--gif">
        <div class="tutorial-popup__gif-area">
          <el-image v-if="content.slide.gif" :src="content.slide.gif" fit="contain" />
          <span v-else>Gif. tutorial</span>
        </div>
        <div class="tutorial-popup__body">
          <p class="tutorial-popup__slide-text">{{ content.slide.text }}</p>
          <div class="tutorial-popup__footer">
            <el-button type="primary" size="large" @click="handleNext">
              {{ content.buttonText }}
            </el-button>
          </div>
        </div>
      </div>

    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useGlobalStore } from '@/stores/GlobalStore';
import { useRegisterStore } from '@/stores/RegisterStore';
import { useHandleCommen } from '@shared/components/ui/handleCommen';
import { useVerConnectFunction } from '@/components/register/VeriftySocialFunction';
import VeriftySocial from '@/components/register/VeriftySocial.vue';
import ajax from '@/utils/Ajax';
import emitter from '@shared/utils/EventBus';
import storefrontToLinkImg from '@/assets/images/storefrontToLink.png';
import linkToStorefrontImg from '@/assets/images/linkToStorefront.png';

const store = useGlobalStore();
const RegisterStore = useRegisterStore();
const router = useRouter();
const { toSlug } = useHandleCommen();
const { findConnect, findBindConnect } = useVerConnectFunction();

const TUTORIAL_SOCIAL_KEY = 'TUTORIAL_POPUP_social_step';

const step = ref(0);
const socialStep = ref(null); // null | '2-3' | '2-4'
const verifySocialRef = ref(null);
const pickHandleValue = ref('');

const merchant = computed(() => store.tutorialQueue[0] || null);
const isLinkOnly = computed(() => merchant.value?.anchor_creator_platform === 'linkonly');
const isStorefront = computed(() => merchant.value?.anchor_creator_platform === 'storefront');
const hasNonMotomHandles = computed(() => {
  const list = store.user?.user_handle_list ?? [];
  return list.some(item => item.from !== 'motom');
});
const loadingData = ref(false);
const content = computed(() => {
  if (isLinkOnly.value) {
    let slideText;
    if (store.project === 'sephora') {
      slideText = store.is_mobile
        ? 'Your previous products, posts, and collections are saved in your archive. To view them, click the Archive icon in the top-left corner of Storefront.'
        : 'Your previous products, posts, and collections are saved in your archive. To view them, click the Archive icon in the top-right corner of the Storefront page.';
    } else if (store.project === 'cj') {
      slideText = store.is_mobile ?
      'Your previous products, posts, and collections are saved in your archive. To view them, click the Archive icon in the top-left corner of the Storefront page.'
      :'Your previous products, posts, and collections are saved in your archive. To view them, click the Archive icon in the top-right corner of the Storefront page.';
    } else {
      slideText = store.is_mobile
        ? 'Your previous products, posts, and collections are saved in your archive. To view them, click the Archive icon in the top-left corner of Manage.'
        : 'Your previous products, posts, and collections are saved in your archive. To view them, click the Archive icon in the top-right corner of the Manage page.';
    }
    return {
      title: 'You are now a Links creator!',
      description: "Here's what's changed — and don't worry, all your previous products, collections, and posts have been saved.",
      manageType: 'links',
      slide: { text: slideText, gif: storefrontToLinkImg },
      buttonText: 'Get started',
    };
  }
  let storefrontSlideText;
  if (store.project === 'cj') {
    storefrontSlideText = store.is_mobile
    ?'Previous links are saved in your archive. To view them, click the Archive icon in the top-left corner of Storefront.'
    :'Previous links are saved in your archive. To view them, click the Archive icon in the top-right corner of Storefront.';
  } 
  else if (store.project === 'sephora') {
    storefrontSlideText = store.is_mobile
      ? 'Previous links are saved in your archive. To view them, click the Archive icon in the top-left corner of Storefront.'
      : 'Previous links are saved in your archive. To view them, click the Archive icon in the top-right corner of Storefront.';
  } else {
    storefrontSlideText = store.is_mobile
      ? 'Previous links are saved in your archive. To view them, click the Archive icon in the top-left corner of Manage.'
      : 'Previous links are saved in your archive. To view them, click the Archive icon in the top-right corner of Manage.';
  }
  return {
    title: "You are now a Storefront creator!",
    description: "Here's what's changed — and don't worry, all your previous links have been saved.",
    manageType: 'storefront',
    slide: { text: storefrontSlideText, gif: linkToStorefrontImg },
    buttonText: 'Get started',
  };
});

const onTutorialQueueUpdated = () => {
  if (merchant.value) {
    const savedStep = window.localStorage.getItem(TUTORIAL_SOCIAL_KEY);
    if (savedStep === '2-3' || savedStep === '2-4') {
      findConnect();
      findBindConnect();
      socialStep.value = !hasNonMotomHandles.value ? '2-4' : '2-3';
      window.localStorage.setItem(TUTORIAL_SOCIAL_KEY, socialStep.value);
      RegisterStore.handleRegisterSocial();
      return;
    }
  } else {
    step.value = 0;
    socialStep.value = null;
  }
};

onMounted(() => emitter.on('tutorial-queue-updated', onTutorialQueueUpdated));
onUnmounted(() => emitter.off('tutorial-queue-updated', onTutorialQueueUpdated));

//+++++++++++++++++++++++++
// functions
//+++++++++++++++++++++++++
const proceedToTutorial = () => {
  const slug = toSlug(merchant.value.merchant_display_name);
  console.log('merchant.value',merchant.value)
  console.log("isLinkOnly.value",isLinkOnly.value)
  if (isLinkOnly.value) {
    router.push(`/manage/${slug}/links`);
    return;
  }else{
    router.push(`/manage/${slug}/products`);
  }
  step.value = 1;
};

const handleGetStarted = async () => {
  if (isStorefront.value && !hasNonMotomHandles.value) {
    window.localStorage.setItem(TUTORIAL_SOCIAL_KEY, '2-3');
    socialStep.value = '2-3';
    await RegisterStore.handleRegisterSocial();
    return;
  }
  proceedToTutorial();
  step.value = 1;
};

const handleSocialNext = async () => {
  await RegisterStore.handleVerSocialPage();
  if (!hasNonMotomHandles.value) {
    socialStep.value = '2-4';
    window.localStorage.setItem(TUTORIAL_SOCIAL_KEY, '2-4');
  } else {
    window.localStorage.removeItem(TUTORIAL_SOCIAL_KEY);
    socialStep.value = null;
    proceedToTutorial();
  }
};

const updateHandle = (val) => {
  pickHandleValue.value = val;
};

const saveHandle = async () => {
  verifySocialRef.value?.confirmHandle();
  const formData = new FormData();
  formData.append('handle', pickHandleValue.value);
  try {
    await ajax.post('update-user-handle', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  } catch {
    // proceed regardless
  }
  window.localStorage.removeItem(TUTORIAL_SOCIAL_KEY);
  socialStep.value = null;
  proceedToTutorial();
};

const handleNext = async () => {
  const formData = new FormData();
  formData.append('merchant_account_ids[]', merchant.value.id);
  try {
    await ajax.post('/overview/update-is-finish-tutorial', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  } catch {}

    loadingData.value = true;
    store
    .getUserMe(true)
    .then((user) => {
      if (user.user_status !== 100) {
        store.clearToken();
        window.location.href = '/login';
        return;
      }
      step.value = 0;
      const unreadTutorials = (user.merchant_accounts || []).filter(
        (m) => m.is_tutorial_read === 0,
      );
      store.setTutorialQueue(unreadTutorials);
      if(unreadTutorials.length > 0){
        emitter.emit('tutorial-queue-updated');
      }
      loadingData.value = false;
      router.push('/');
    })
    .catch((err) => {
      loadingData.value = false;
    });
  
};

</script>

<style lang="scss">
.tutorial-popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
}

.tutorial-popup {
  background: var(--Black-00);
  border-radius: var(--radius-medium);
  width: 100%;
  max-width: 657px;
  overflow: hidden;

  &--intro {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 48px;
    text-align: left;
    gap: 24px;

    .tutorial-popup__logo {
      width: 80px;
      height: 80px;
      border-radius: var(--radius);
      margin-bottom: 8px;
      color: var(--Text-primary);
    }

    .tutorial-popup__title {
      font: var(--demi-24);
      width: 100%;
    }

    .tutorial-popup__desc {
      font: var(--medium-14);
      color:var(--Text-secondary);
      text-align: left;
      width: 100%;
    }

    .tutorial-popup__btn {
      width: 100%;
      margin-top: 8px;
    }
  }

  &--gif {
    .tutorial-popup__gif-area {
      width: 100%;
      height: 443px;
      background: var(--Black-20);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--Text-default);
      font: var(--medium-16);

      .ui-image {
        width: 100%;
        height: 100%;
      }


      @media (max-width: $mobile_size) {
        height: unset;
        //aspect-ratio: 1 / 1;
      }
    }

    .tutorial-popup__body {
      padding: 24px;
    }

    .tutorial-popup__slide-text {
      font: var(--medium-14);
      color: var(--Text-primary);
      min-height: 60px;
    }

    .tutorial-popup__footer {
      display: flex;
      justify-content: flex-end;
      margin-top: 24px;
      .ui-button{
        width: 100%;
      }
    }
  }

  &--social {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 32px;
    gap: 16px;

    h2 {
      font: var(--demi-24);
    }

    .hint-text {
      font: var(--medium-14);
      color: var(--Text-secondary);

      .ui-link {
        font: var(--medium-14);
      }
    }

    .handle-select-box{
      .msg-box{
        margin: 12px 0;
      }
    }

    .btn-box{
      width: 100%;
    }
  }
}
</style>
